import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  DocumentDuplicateIcon,
  LanguageIcon,
  PlusIcon,
  QrCodeIcon,
  SparklesIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import DataTable from '../../components/ui/DataTable';
import ResponsiveModal from '../../components/ui/ResponsiveModal';
import FilterPanel from '../../components/ui/FilterPanel';
import ActionToolbar from '../../components/ui/ActionToolbar';
import IconActionButton from '../../components/ui/IconActionButton';
import foodBeverageApi from '../../api/foodBeverageApi';
import { API_BASE_URL, referenceAPI } from '../../api/api';

const translationLanguages = [
  { code: 'tr', label: 'TR' },
  { code: 'en', label: 'EN' },
  { code: 'de', label: 'DE' },
  { code: 'ru', label: 'RU' },
];

const createEmptyTranslations = () => ({
  tr: '',
  en: '',
  de: '',
  ru: '',
});

const normalizeTranslations = (value, fallback = '') => {
  const next = createEmptyTranslations();

  if (value && typeof value === 'object' && !Array.isArray(value)) {
    translationLanguages.forEach(({ code }) => {
      next[code] = String(value[code] || '').trim();
    });
  }

  const fallbackValue = String(fallback || '').trim();
  if (fallbackValue && !next.tr) {
    next.tr = fallbackValue;
  }

  return next;
};

const sectionMeta = {
  hotels: {
    title: 'Food & Beverage - Oteller',
    emptyPayload: {
      hotel_id: '',
      name: '',
      name_translations: createEmptyTranslations(),
      qr_slug: '',
      image: '',
      image_file: null,
    },
  },
  'product-groups': {
    title: 'Food & Beverage - Ürün Grupları',
    emptyPayload: {
      qr_hotel: '',
      name: '',
      name_translations: createEmptyTranslations(),
      display_order: 1,
      image: '',
      image_file: null,
    },
  },
  areas: {
    title: 'Food & Beverage - Alanlar',
    emptyPayload: {
      qr_hotel: '',
      product_group: '',
      name: '',
      name_translations: createEmptyTranslations(),
      display_order: 1,
      image: '',
      image_file: null,
      show_product_images: true,
      show_product_prices: true,
    },
  },
  products: {
    title: 'Food & Beverage - Ürünler',
    emptyPayload: {
      qr_hotel: '',
      product_group: '',
      area: '',
      name: '',
      name_translations: createEmptyTranslations(),
      description: '',
      description_translations: createEmptyTranslations(),
      price: 0,
      image_url: '',
      display_order: 1,
      is_active: true,
    },
  },
};

const getEmptyPayload = (section) =>
  JSON.parse(JSON.stringify(sectionMeta[section]?.emptyPayload || sectionMeta.hotels.emptyPayload));

function FoodBeveragePage({ section = 'hotels' }) {
  const apiOrigin = API_BASE_URL.endsWith('/api') ? API_BASE_URL.slice(0, -4) : API_BASE_URL;
  const resolveMediaUrl = (value) => {
    if (!value) return '';
    const raw = String(value).trim();
    if (!raw) return '';
    if (/^https?:\/\//i.test(raw)) {
      try {
        const parsed = new URL(raw);
        const isLocalHostUrl = ['localhost', '127.0.0.1'].includes(parsed.hostname);
        const isCurrentHostLocal = ['localhost', '127.0.0.1'].includes(window.location.hostname);
        if (isLocalHostUrl && !isCurrentHostLocal) {
          return `${window.location.protocol}//${window.location.host}${parsed.pathname}${parsed.search || ''}`;
        }
      } catch (error) {
        return raw;
      }
      return raw;
    }
    if (raw.startsWith('blob:') || raw.startsWith('data:')) return raw;
    return `${apiOrigin}${raw.startsWith('/') ? raw : `/${raw}`}`;
  };

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedRow, setSelectedRow] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [payload, setPayload] = useState(getEmptyPayload(section));
  const [aiLoading, setAiLoading] = useState(false);

  const [qrHotels, setQrHotels] = useState([]);
  const [productGroups, setProductGroups] = useState([]);
  const [areas, setAreas] = useState([]);
  const [products, setProducts] = useState([]);
  const [systemHotels, setSystemHotels] = useState([]);
  const [areaDraftProducts, setAreaDraftProducts] = useState([]);
  const [filterPanelOpen, setFilterPanelOpen] = useState({});
  const [filtersBySection, setFiltersBySection] = useState({});
  const [imagePreviewUrl, setImagePreviewUrl] = useState('');
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [qrData, setQrData] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);
  const [qrWatermark, setQrWatermark] = useState(false);
  const [qrLogoFile, setQrLogoFile] = useState(null);
  const qrLogoInputRef = useRef(null);

  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [copySourceQrHotelId, setCopySourceQrHotelId] = useState('');
  const [sourceAreaIdForCopy, setSourceAreaIdForCopy] = useState('');
  const [copyLoading, setCopyLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [modalSuccessMessage, setModalSuccessMessage] = useState('');
  const [modalErrorMessage, setModalErrorMessage] = useState('');
  const [areaDraftSelectedTempIds, setAreaDraftSelectedTempIds] = useState([]);

  const title = sectionMeta[section]?.title || sectionMeta.hotels.title;

  const getFilterFieldsFromColumns = (cols) =>
    (cols || []).map((col) => ({ key: col.accessorKey, label: col.header })).filter((f) => f.key);

  const getFieldGroup = (fieldKey) => {
    const key = String(fieldKey || '').toLowerCase();
    if (key.includes('order') || key.includes('sıra')) return 'Sıra';
    if (key.includes('price') || key.includes('fiyat')) return 'Fiyat';
    if (key.includes('active') || key.includes('aktif')) return 'Durum';
    if (key.includes('hotel') || key.includes('grup') || key.includes('area') || key.includes('alan')) return 'Otel / Grup / Alan';
    return 'Genel';
  };

  const getLookupOptions = (rows, key) => {
    const uniqueValues = [
      ...new Set(
        rows.map((row) => row?.[key]).filter((value) => value != null && value !== '')
      ),
    ];
    return uniqueValues.map((value) => ({ label: String(value), value: String(value) }));
  };

  const buildFilterPlaceholder = (label, mode = 'text') => {
    if (mode === 'select') return `${label} / Seçiniz`;
    if (mode === 'date') return `${label} / Tarih`;
    return `${label} / Filtrele`;
  };

  const toFormData = (source, keys, fileKey = 'image_file') => {
    const formData = new FormData();
    keys.forEach((key) => {
      const value = source?.[key];
      if (value === undefined || value === null || value === '') return;
      const isPlainObject = value && typeof value === 'object' && !(value instanceof File);
      formData.append(key, isPlainObject ? JSON.stringify(value) : value);
    });
    const imageFile = source?.[fileKey];
    if (imageFile instanceof File) {
      formData.append('image', imageFile);
    }
    return formData;
  };

  const getFilteredRows = (sectionKey, rows, filterFields) => {
    const sectionFilters = filtersBySection[sectionKey] || {};
    const activeEntries = Object.entries(sectionFilters).filter(
      ([, value]) => String(value || '').trim() !== ''
    );
    if (!activeEntries.length) return rows;
    return rows.filter((row) =>
      activeEntries.every(([key, value]) => {
        const filterValue = String(value || '').toLowerCase();
        const rowVal = row?.[key];
        const rowStr =
          rowVal === true ? 'evet' : rowVal === false ? 'hayır' : String(rowVal ?? '').toLowerCase();
        return rowStr.includes(filterValue);
      })
    );
  };

  const renderFilterPanel = (sectionKey, filterFields, dataRows, toolbar = null) => {
    const isOpen = Boolean(filterPanelOpen[sectionKey]);
    const sectionFilters = filtersBySection[sectionKey] || {};
    const groupedFields = (filterFields || []).reduce((acc, field) => {
      const groupName = getFieldGroup(field.key);
      if (!acc[groupName]) acc[groupName] = [];
      acc[groupName].push(field);
      return acc;
    }, {});

    return (
      <FilterPanel
        title="Filters"
        isOpen={isOpen}
        onToggle={() => setFilterPanelOpen((prev) => ({ ...prev, [sectionKey]: !isOpen }))}
        headerRight={toolbar}
      >
        <div className="mt-2 grid grid-cols-1 xl:grid-cols-2 gap-2">
            {Object.entries(groupedFields).map(([groupName, fields]) => (
              <div
                key={`${sectionKey}-${groupName}`}
                className="border border-gray-200 dark:border-gray-700 rounded-md p-2"
              >
                <p className="text-[11px] font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  {groupName}
                </p>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-1.5">
                  {fields.map((field) => {
                    const keyLower = String(field.key || '').toLowerCase();
                    const isDateField =
                      keyLower.endsWith('_date') ||
                      keyLower.endsWith('_at') ||
                      keyLower === 'date';
                    const lookupCandidates = getLookupOptions(dataRows, field.key);
                    const canUseLookup =
                      !isDateField &&
                      lookupCandidates.length > 0 &&
                      lookupCandidates.length <= 30;

                    if (isDateField) {
                      return (
                        <fieldset
                          key={`${sectionKey}-${field.key}`}
                          className="border border-gray-300 dark:border-gray-600 rounded-md px-2 pb-1 pt-0.5 bg-white dark:bg-gray-700"
                        >
                          <legend className="px-1 text-[10px] text-gray-500 dark:text-gray-400 leading-none">
                            {field.label}
                          </legend>
                          <input
                            type="date"
                            value={sectionFilters[field.key] || ''}
                            onChange={(e) => {
                              const nextValue = e.target.value;
                              setFiltersBySection((prev) => ({
                                ...prev,
                                [sectionKey]: {
                                  ...(prev[sectionKey] || {}),
                                  [field.key]: nextValue,
                                },
                              }));
                            }}
                            className="w-full bg-transparent text-[11px] text-gray-900 dark:text-gray-100 outline-none"
                            title={buildFilterPlaceholder(field.label, 'date')}
                          />
                        </fieldset>
                      );
                    }

                    if (canUseLookup) {
                      return (
                        <fieldset
                          key={`${sectionKey}-${field.key}`}
                          className="border border-gray-300 dark:border-gray-600 rounded-md px-2 pb-1 pt-0.5 bg-white dark:bg-gray-700"
                        >
                          <legend className="px-1 text-[10px] text-gray-500 dark:text-gray-400 leading-none">
                            {field.label}
                          </legend>
                          <select
                            value={sectionFilters[field.key] || ''}
                            onChange={(e) => {
                              const nextValue = e.target.value;
                              setFiltersBySection((prev) => ({
                                ...prev,
                                [sectionKey]: {
                                  ...(prev[sectionKey] || {}),
                                  [field.key]: nextValue,
                                },
                              }));
                            }}
                            className="w-full bg-transparent text-[11px] text-gray-900 dark:text-gray-100 outline-none"
                          >
                            <option value="">Seçiniz</option>
                            {lookupCandidates.map((opt) => (
                              <option
                                key={`${sectionKey}-${field.key}-${opt.value}`}
                                value={opt.value}
                              >
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        </fieldset>
                      );
                    }

                    return (
                      <fieldset
                        key={`${sectionKey}-${field.key}`}
                        className="border border-gray-300 dark:border-gray-600 rounded-md px-2 pb-1 pt-0.5 bg-white dark:bg-gray-700"
                      >
                        <legend className="px-1 text-[10px] text-gray-500 dark:text-gray-400 leading-none">
                          {field.label}
                        </legend>
                        <input
                          value={sectionFilters[field.key] || ''}
                          onChange={(e) => {
                            const nextValue = e.target.value;
                            setFiltersBySection((prev) => ({
                              ...prev,
                              [sectionKey]: {
                                ...(prev[sectionKey] || {}),
                                [field.key]: nextValue,
                              },
                            }));
                          }}
                          className="w-full bg-transparent text-[11px] text-gray-900 dark:text-gray-100 outline-none"
                        />
                      </fieldset>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
      </FilterPanel>
    );
  };

  const loadAll = async () => {
    setIsLoading(true);
    setError('');
    try {
      const [hotelsResp, groupsResp, areasResp, productsResp, refHotelsResp] = await Promise.all([
        foodBeverageApi.getQrHotels(),
        foodBeverageApi.getProductGroups(),
        foodBeverageApi.getAreas(),
        foodBeverageApi.getProducts(),
        referenceAPI.getHotels(),
      ]);

      setQrHotels((hotelsResp?.data || []).map((item) => ({ ...item, image: resolveMediaUrl(item.image) })));
      setProductGroups((groupsResp?.data || []).map((item) => ({ ...item, image: resolveMediaUrl(item.image) })));
      setAreas((areasResp?.data || []).map((item) => ({ ...item, image: resolveMediaUrl(item.image) })));
      setProducts(productsResp?.data || []);
      setSystemHotels(refHotelsResp?.data || []);
    } catch (err) {
      setError(err.message || 'Veriler yüklenemedi.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section]);

  useEffect(() => {
    setPayload(getEmptyPayload(section));
    setSelectedRow(null);
    setIsModalOpen(false);
    setModalSuccessMessage('');
    setModalErrorMessage('');
    setAreaDraftProducts([]);
  }, [section]);

  const closeMainModal = () => {
    setIsModalOpen(false);
    setModalSuccessMessage('');
    setModalErrorMessage('');
  };

  useEffect(() => {
    if (!payload?.image_file) {
      setImagePreviewUrl(payload?.image || '');
      return undefined;
    }

    const objectUrl = URL.createObjectURL(payload.image_file);
    setImagePreviewUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [payload?.image_file, payload?.image]);

  const rows = useMemo(() => {
    if (section === 'hotels') return qrHotels;
    if (section === 'product-groups') return productGroups;
    if (section === 'areas') return areas;
    if (section === 'products') return products;
    return [];
  }, [areas, productGroups, products, qrHotels, section]);

  const syncPayloadTranslations = (row = {}, basePayload = {}) => ({
    ...basePayload,
    ...row,
    name: row.name || basePayload.name || '',
    description: row.description || basePayload.description || '',
    name_translations: normalizeTranslations(row.name_translations, row.name || basePayload.name || ''),
    description_translations: normalizeTranslations(
      row.description_translations,
      row.description || basePayload.description || ''
    ),
  });

  const handleTranslationChange = (fieldName, language, value) => {
    setPayload((prev) => {
      const nextTranslations = normalizeTranslations(prev[fieldName], prev[fieldName === 'name_translations' ? 'name' : 'description']);
      nextTranslations[language] = value;
      const nextPayload = {
        ...prev,
        [fieldName]: nextTranslations,
      };

      if (fieldName === 'name_translations') {
        nextPayload.name = nextTranslations.tr || '';
      }

      if (fieldName === 'description_translations') {
        nextPayload.description = nextTranslations.tr || '';
      }

      return nextPayload;
    });
  };

  const renderTranslationFields = ({ title, fieldName, multiline = false }) => (
    <div className="space-y-2 rounded-lg border border-gray-200 dark:border-gray-700 p-3">
      <div>
        <h4 className="text-sm font-medium text-gray-900 dark:text-white">{title}</h4>
        <p className="text-[11px] text-gray-500 dark:text-gray-400">
          <span className="font-mono">TR</span> ana metindir, diğer diller isteğe bağlıdır.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {translationLanguages.map(({ code, label }) => (
          <div key={`${fieldName}-${code}`} className="space-y-1">
            <label className="text-xs text-gray-600 dark:text-gray-300">{label}</label>
            {multiline ? (
              <textarea
                value={payload?.[fieldName]?.[code] || ''}
                onChange={(e) => handleTranslationChange(fieldName, code, e.target.value)}
                rows={code === 'tr' ? 3 : 2}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
            ) : (
              <input
                value={payload?.[fieldName]?.[code] || ''}
                onChange={(e) => handleTranslationChange(fieldName, code, e.target.value)}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );

  const columns = useMemo(() => {
    if (section === 'hotels') {
      return [
        {
          accessorKey: 'image',
          header: 'Fotoğraf',
          cell: ({ row }) => row.original.image ? (
            <img
              src={row.original.image}
              alt={row.original.name || 'hotel'}
              className="h-8 w-12 object-cover rounded border border-gray-200 dark:border-gray-700"
            />
          ) : '-',
        },
        { accessorKey: 'name', header: 'Ad' },
        { accessorKey: 'hotel_name', header: 'Sistem Oteli' },
        { accessorKey: 'qr_slug', header: 'QR Path' },
        { accessorKey: 'updated_name', header: 'Güncelleyen' },
      ];
    }
    if (section === 'product-groups') {
      return [
        {
          accessorKey: 'image',
          header: 'Fotoğraf',
          cell: ({ row }) => row.original.image ? (
            <img
              src={row.original.image}
              alt={row.original.name || 'group'}
              className="h-8 w-12 object-cover rounded border border-gray-200 dark:border-gray-700"
            />
          ) : '-',
        },
        { accessorKey: 'name', header: 'Grup Adı' },
        { accessorKey: 'hotel_name', header: 'QR Otel' },
        { accessorKey: 'display_order', header: 'Sıra' },
        { accessorKey: 'updated_name', header: 'Güncelleyen' },
      ];
    }
    if (section === 'areas') {
      return [
        {
          accessorKey: 'image',
          header: 'Fotoğraf',
          cell: ({ row }) => row.original.image ? (
            <img
              src={row.original.image}
              alt={row.original.name || 'area'}
              className="h-8 w-12 object-cover rounded border border-gray-200 dark:border-gray-700"
            />
          ) : '-',
        },
        { accessorKey: 'name', header: 'Alan Adı' },
        { accessorKey: 'hotel_name', header: 'QR Otel' },
        { accessorKey: 'product_group_name', header: 'Ürün Grubu' },
        {
          accessorKey: 'show_product_images',
          header: 'Ürün Fotoğrafı',
          cell: ({ row }) => (row.original.show_product_images === false ? 'Hayır' : 'Evet'),
        },
        {
          accessorKey: 'show_product_prices',
          header: 'Ürün Fiyatı',
          cell: ({ row }) => (row.original.show_product_prices === false ? 'Hayır' : 'Evet'),
        },
        { accessorKey: 'display_order', header: 'Sıra' },
      ];
    }
    return [
      { accessorKey: 'name', header: 'Ürün' },
      { accessorKey: 'hotel_name', header: 'QR Otel' },
      { accessorKey: 'product_group_name', header: 'Grup' },
      { accessorKey: 'area_name', header: 'Alan' },
      { accessorKey: 'price', header: 'Fiyat' },
      {
        accessorKey: 'is_active',
        header: 'Aktif',
        cell: ({ row }) => (row.original.is_active ? 'Evet' : 'Hayır'),
      },
    ];
  }, [section]);

  const openCreateModal = () => {
    setSelectedRow(null);
    setModalSuccessMessage('');
    setModalErrorMessage('');
    const initialPayload = getEmptyPayload(section);
    setPayload(initialPayload);
    if (section === 'areas') {
      setAreaDraftSelectedTempIds([]);
      setAreaDraftProducts([
        {
          temp_id: `new-${Date.now()}`,
          id: null,
          product_group: '',
          name: '',
          name_translations: createEmptyTranslations(),
          description: '',
          description_translations: createEmptyTranslations(),
          price: 0,
          display_order: 1,
          is_active: true,
        },
      ]);
    }
    setIsModalOpen(true);
  };

  const openEditModal = (row) => {
    setSelectedRow(row);
    setModalSuccessMessage('');
    setModalErrorMessage('');
    const basePayload = getEmptyPayload(section);
    setPayload(
      syncPayloadTranslations(row, {
        ...basePayload,
        area: row.area || '',
        product_group: row.product_group || '',
        qr_hotel: row.qr_hotel || '',
        hotel_id: row.hotel_id || row.hotel || '',
        image: row.image || '',
        image_file: null,
        show_product_images: row.show_product_images !== false,
        show_product_prices: row.show_product_prices !== false,
      })
    );
    if (section === 'areas') {
      setAreaDraftSelectedTempIds([]);
      const relatedProducts = products
        .filter((item) => String(item.area) === String(row.id))
        .map((item) => ({
          temp_id: `existing-${item.id}`,
          id: item.id,
          product_group: item.product_group || '',
          name: item.name || '',
          name_translations: normalizeTranslations(item.name_translations, item.name || ''),
          description: item.description || '',
          description_translations: normalizeTranslations(item.description_translations, item.description || ''),
          price: item.price ?? 0,
          display_order: item.display_order ?? 1,
          is_active: item.is_active ?? true,
        }));
      setAreaDraftProducts(
        relatedProducts.length
          ? relatedProducts
          : [
              {
                temp_id: `new-${Date.now()}`,
                id: null,
                product_group: '',
                name: '',
                name_translations: createEmptyTranslations(),
                description: '',
                description_translations: createEmptyTranslations(),
                price: 0,
                display_order: 1,
                is_active: true,
              },
            ]
      );
    }
    setIsModalOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedRow?.id) return;
    try {
      if (section === 'hotels') await foodBeverageApi.deleteQrHotel(selectedRow.id);
      if (section === 'product-groups') await foodBeverageApi.deleteProductGroup(selectedRow.id);
      if (section === 'areas') await foodBeverageApi.deleteArea(selectedRow.id);
      if (section === 'products') await foodBeverageApi.deleteProduct(selectedRow.id);
      await loadAll();
      setSelectedRow(null);
    } catch (err) {
      setError(err.message || 'Silme işlemi başarısız.');
    }
  };

  const fetchQrWithOptions = async (areaId, logoFile, watermark) => {
    const resp = logoFile
      ? await foodBeverageApi.getAreaQrCodeWithLogo(areaId, logoFile, { watermark })
      : await foodBeverageApi.getAreaQrCode(areaId, { watermark });
    if (!resp?.success) throw new Error(resp?.message || 'QR kod oluşturulamadı.');
    const payload = resp.data && typeof resp.data === 'object' ? resp.data : resp;
    if (!payload?.image_base64) throw new Error(resp?.message || 'QR kod oluşturulamadı.');
    return {
      image_base64: payload.image_base64,
      url: payload.url,
      area_name: payload.area_name,
      hotel_name: payload.hotel_name,
    };
  };

  const handleGenerateQr = async () => {
    if (section !== 'areas' || !selectedRow?.id) return;
    setQrLoading(true);
    setQrData(null);
    setQrModalOpen(true);
    setQrLogoFile(null);
    try {
      const data = await fetchQrWithOptions(selectedRow.id, null, qrWatermark);
      setQrData(data);
    } catch (err) {
      setError(err.message || 'QR kod oluşturulamadı.');
    } finally {
      setQrLoading(false);
    }
  };

  const handleDownloadQr = () => {
    if (!qrData?.image_base64) return;
    const link = document.createElement('a');
    link.href = qrData.image_base64;
    link.download = `qr-${(qrData.area_name || 'alan').replace(/\s+/g, '-')}.png`;
    link.click();
  };

  const handleQrLogoUpload = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file || section !== 'areas' || !selectedRow?.id) return;
    if (!file.type.startsWith('image/')) {
      setError('Lütfen bir görsel dosyası seçin (PNG, JPG vb.).');
      return;
    }
    setQrLogoFile(file);
    setQrLoading(true);
    try {
      const data = await fetchQrWithOptions(selectedRow.id, file, qrWatermark);
      setQrData(data);
    } catch (err) {
      setError(err.message || 'Logo yüklenirken hata oluştu.');
    } finally {
      setQrLoading(false);
      if (qrLogoInputRef.current) qrLogoInputRef.current.value = '';
    }
  };

  const openCopyProductsModal = () => {
    if (section !== 'areas' || !selectedRow?.id) return;
    setCopySourceQrHotelId(selectedRow.qr_hotel != null ? String(selectedRow.qr_hotel) : '');
    setSourceAreaIdForCopy('');
    setSuccessMessage('');
    setCopyModalOpen(true);
  };

  const handleCopyProductsConfirm = async () => {
    if (section !== 'areas' || !selectedRow?.id) return;
    if (String(copySourceQrHotelId || '') !== String(selectedRow.qr_hotel || '')) {
      setError('Kaynak ve hedef alan aynı QR oteline ait olmalıdır.');
      return;
    }
    const sourceId = String(sourceAreaIdForCopy || '').trim();
    if (!sourceId) {
      setError('Kaynak alan seçiniz.');
      return;
    }
    const targetProductCount = products.filter(
      (item) => String(item.area) === String(selectedRow.id)
    ).length;
    if (targetProductCount > 0) {
      const ok = window.confirm(
        'Hedef alanda zaten ürünler var. Kaynak alandaki ürünler eklenecek (mevcut ürünler silinmez). Devam etmek istiyor musunuz?'
      );
      if (!ok) return;
    }
    setCopyLoading(true);
    setError('');
    setSuccessMessage('');
    try {
      const resp = await foodBeverageApi.copyAreaProducts(selectedRow.id, {
        source_area_id: Number(sourceId),
      });
      const count =
        typeof resp?.copied_count === 'number'
          ? resp.copied_count
          : resp?.data?.copied_count;
      const n = Number.isFinite(count) ? count : 0;
      setSuccessMessage(
        n > 0 ? `${n} ürün hedef alana kopyalandı.` : 'Kopyalanacak ürün yoktu (kaynak alan boş).'
      );
      setCopyModalOpen(false);
      await loadAll();
    } catch (err) {
      setError(err.message || 'Ürünler kopyalanamadı.');
    } finally {
      setCopyLoading(false);
    }
  };

  const handleQrWatermarkToggle = async () => {
    if (section !== 'areas' || !selectedRow?.id || !qrData) return;
    const nextWatermark = !qrWatermark;
    setQrWatermark(nextWatermark);
    setQrLoading(true);
    try {
      const data = await fetchQrWithOptions(selectedRow.id, qrLogoFile || null, nextWatermark);
      setQrData(data);
    } catch (err) {
      setError(err.message || 'QR güncellenemedi.');
      setQrWatermark(qrWatermark);
    } finally {
      setQrLoading(false);
    }
  };

  const handleTranslateCurrentRecord = async () => {
    if (!selectedRow?.id) {
      setError('Önce çevrilecek kaydı seçiniz.');
      return;
    }

    const actionMap = {
      hotels: foodBeverageApi.translateQrHotel,
      'product-groups': foodBeverageApi.translateProductGroup,
      areas: foodBeverageApi.translateArea,
      products: foodBeverageApi.translateProduct,
    };

    const action = actionMap[section];
    if (!action) return;

    setAiLoading(true);
    setError('');
    try {
      const response = await action(selectedRow.id);
      const translatedRecord =
        response?.data && typeof response.data === 'object' && !Array.isArray(response.data)
          ? response.data
          : response?.data?.data ?? null;
      if (translatedRecord) {
        setSelectedRow(translatedRecord);
        setPayload((prev) =>
          syncPayloadTranslations(translatedRecord, {
            ...prev,
            image_file: null,
            ...(section === 'products'
              ? { image_url: translatedRecord.image_url ?? prev.image_url ?? '' }
              : { image: translatedRecord.image ?? prev.image ?? '' }),
          })
        );
      }
      await loadAll();
      setSuccessMessage('Eksik çeviriler AI ile dolduruldu.');
    } catch (err) {
      setError(err.message || 'Çeviri üretilemedi.');
    } finally {
      setAiLoading(false);
    }
  };

  const handleTranslateSelectedHotel = async () => {
    if (section !== 'hotels' || !selectedRow?.id) {
      setError('Önce çeviri üretilecek QR oteli seçiniz.');
      return;
    }

    setAiLoading(true);
    setError('');
    try {
      const response = await foodBeverageApi.translateQrHotelMissing(selectedRow.id);
      const summary =
        response?.data && typeof response.data === 'object' && !Array.isArray(response.data)
          ? response.data
          : response?.data?.data ?? {};
      await loadAll();
      setSuccessMessage(
        `${summary.records_updated || 0} kayıt güncellendi, ${summary.products_processed || 0} ürün işlendi.`
      );
    } catch (err) {
      setError(err.message || 'Toplu çeviri üretilemedi.');
    } finally {
      setAiLoading(false);
    }
  };

  const handleTranslateAllProductsMissing = async () => {
    if (section !== 'products') return;

    const scopedHotelId = selectedRow?.qr_hotel;
    if (scopedHotelId == null || scopedHotelId === '') {
      const ok = window.confirm(
        'Hiç ürün seçmediniz. Tüm QR otellerdeki ürünler taranacak; çok sayıda kayıt varsa uzun sürebilir. İşlem küçük parçalar (sunucu/IIS zaman aşımı için) tekrarlanır. Devam edilsin mi?'
      );
      if (!ok) return;
    }

    // IIS/proxy genelde uzun tek isteği keser; 50 ürün × AI çağrıları 502 üretebilir. 10–12 güvenli aralık.
    const batchSize = 10;
    const maxBatchAttempts = 4;
    const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

    const translateBatchWithRetry = async (offsetParam, limitParam) => {
      let lastErr;
      for (let attempt = 1; attempt <= maxBatchAttempts; attempt += 1) {
        try {
          return await foodBeverageApi.translateProductsMissing(
            scopedHotelId != null && scopedHotelId !== '' ? scopedHotelId : null,
            { offset: offsetParam, limit: limitParam }
          );
        } catch (err) {
          lastErr = err;
          const msg = String(err?.message || '').toLowerCase();
          const transient =
            msg.includes('failed to fetch') ||
            msg.includes('network error') ||
            msg.includes('502') ||
            msg.includes('503') ||
            msg.includes('504') ||
            msg.includes('gateway') ||
            msg.includes('timeout') ||
            msg.includes('bad gateway');
          if (!transient || attempt === maxBatchAttempts) {
            throw err;
          }
          setSuccessMessage(
            `Geçici sunucu hatası (${attempt}/${maxBatchAttempts}), ${attempt * 2} sn sonra tekrar deneniyor…`
          );
          await sleep(2000 * attempt);
        }
      }
      throw lastErr;
    };

    setAiLoading(true);
    setError('');
    setSuccessMessage('');
    try {
      let offset = 0;
      let totalProcessed = 0;
      let totalUpdated = 0;
      let totalFailed = 0;
      let totalCount = null;
      let hasMore = true;
      while (hasMore) {
        const response = await translateBatchWithRetry(offset, batchSize);
        const summary =
          response?.data && typeof response.data === 'object' && !Array.isArray(response.data)
            ? response.data
            : response?.data?.data ?? {};
        totalProcessed += summary.products_processed ?? 0;
        totalUpdated += summary.products_updated ?? 0;
        totalFailed += summary.products_failed ?? 0;
        if (totalCount == null && summary.total_count != null) {
          totalCount = summary.total_count;
        }
        hasMore = Boolean(summary.has_more);
        if (hasMore) {
          offset = summary.next_offset ?? offset + batchSize;
        }
        if (hasMore) {
          setSuccessMessage(
            `İşleniyor… ${totalProcessed} ürün${totalCount != null ? ` / ${totalCount}` : ''} (parça başına ${batchSize}).`
          );
          await sleep(400);
        }
      }
      await loadAll();
      setSuccessMessage(
        `Toplam ${totalProcessed} ürün tarandı, ${totalUpdated} üründe eksik çeviri güncellendi, ${totalFailed} ürün çevrilemedi${
          totalCount != null ? ` (${totalCount} kayıt kapsamında).` : '.'
        }`
      );
    } catch (err) {
      setError(err.message || 'Toplu ürün çevirisi başarısız.');
    } finally {
      setAiLoading(false);
    }
  };

  const handleTranslateAllProductDescriptionsMissing = async () => {
    if (section !== 'products') return;

    const scopedHotelId = selectedRow?.qr_hotel;
    if (scopedHotelId == null || scopedHotelId === '') {
      const ok = window.confirm(
        'Hiç ürün seçmediniz. Tüm QR otellerdeki ürün açıklamaları taranacak; çok sayıda kayıt varsa uzun sürebilir. İşlem küçük parçalar halinde tekrarlanır. Devam edilsin mi?'
      );
      if (!ok) return;
    }

    const batchSize = 10;
    const maxBatchAttempts = 4;
    const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

    const translateBatchWithRetry = async (offsetParam, limitParam) => {
      let lastErr;
      for (let attempt = 1; attempt <= maxBatchAttempts; attempt += 1) {
        try {
          return await foodBeverageApi.translateProductDescriptionsMissing(
            scopedHotelId != null && scopedHotelId !== '' ? scopedHotelId : null,
            { offset: offsetParam, limit: limitParam }
          );
        } catch (err) {
          lastErr = err;
          const msg = String(err?.message || '').toLowerCase();
          const transient =
            msg.includes('failed to fetch') ||
            msg.includes('network error') ||
            msg.includes('502') ||
            msg.includes('503') ||
            msg.includes('504') ||
            msg.includes('gateway') ||
            msg.includes('timeout') ||
            msg.includes('bad gateway');
          if (!transient || attempt === maxBatchAttempts) {
            throw err;
          }
          setSuccessMessage(
            `Geçici sunucu hatası (${attempt}/${maxBatchAttempts}), ${attempt * 2} sn sonra tekrar deneniyor…`
          );
          await sleep(2000 * attempt);
        }
      }
      throw lastErr;
    };

    setAiLoading(true);
    setError('');
    setSuccessMessage('');
    try {
      let offset = 0;
      let totalProcessed = 0;
      let totalUpdated = 0;
      let totalFailed = 0;
      let totalCount = null;
      let hasMore = true;
      while (hasMore) {
        const response = await translateBatchWithRetry(offset, batchSize);
        const summary =
          response?.data && typeof response.data === 'object' && !Array.isArray(response.data)
            ? response.data
            : response?.data?.data ?? {};
        totalProcessed += summary.products_processed ?? 0;
        totalUpdated += summary.products_updated ?? 0;
        totalFailed += summary.products_failed ?? 0;
        if (totalCount == null && summary.total_count != null) {
          totalCount = summary.total_count;
        }
        hasMore = Boolean(summary.has_more);
        if (hasMore) {
          offset = summary.next_offset ?? offset + batchSize;
        }
        if (hasMore) {
          setSuccessMessage(
            `Açıklamalar işleniyor… ${totalProcessed} ürün${totalCount != null ? ` / ${totalCount}` : ''} (parça başına ${batchSize}).`
          );
          await sleep(400);
        }
      }
      await loadAll();
      setSuccessMessage(
        `Açıklama çevirisi: toplam ${totalProcessed} ürün tarandı, ${totalUpdated} üründe eksik açıklama çevirisi güncellendi, ${totalFailed} ürün çevrilemedi${
          totalCount != null ? ` (${totalCount} kayıt kapsamında).` : '.'
        }`
      );
    } catch (err) {
      setError(err.message || 'Toplu ürün açıklaması çevirisi başarısız.');
    } finally {
      setAiLoading(false);
    }
  };

  const handleSave = async () => {
    setSaveLoading(true);
    try {
      setModalErrorMessage('');
      setModalSuccessMessage('');
      const body = { ...payload };
      if (section === 'hotels') {
        const hotelFormData = toFormData(body, ['hotel_id', 'name', 'name_translations', 'qr_slug']);
        if (selectedRow) await foodBeverageApi.updateQrHotel(selectedRow.id, hotelFormData);
        else await foodBeverageApi.createQrHotel(hotelFormData);
      }
      if (section === 'product-groups') {
        const groupFormData = toFormData(body, ['qr_hotel', 'name', 'name_translations', 'display_order']);
        if (selectedRow) await foodBeverageApi.updateProductGroup(selectedRow.id, groupFormData);
        else await foodBeverageApi.createProductGroup(groupFormData);
      }
      if (section === 'areas') {
        let areaId = selectedRow?.id;
        const areaFormData = toFormData(
          body,
          [
            'qr_hotel',
            'product_group',
            'name',
            'name_translations',
            'display_order',
            'show_product_images',
            'show_product_prices',
          ]
        );
        if (selectedRow) {
          await foodBeverageApi.updateArea(selectedRow.id, areaFormData);
        } else {
          const createAreaResponse = await foodBeverageApi.createArea(areaFormData);
          areaId = createAreaResponse?.data?.id || createAreaResponse?.id;
        }

        const validProducts = areaDraftProducts.filter(
          (item) => item && item.name && item.product_group
        );

        const currentProductIds = validProducts.filter((item) => item.id).map((item) => item.id);
        if (selectedRow) {
          const existingAreaProducts = products.filter(
            (item) => String(item.area) === String(selectedRow.id)
          );
          const removeCandidates = existingAreaProducts.filter(
            (item) => !currentProductIds.includes(item.id)
          );
          await Promise.all(removeCandidates.map((item) => foodBeverageApi.deleteProduct(item.id)));
        }

        await Promise.all(
          validProducts.map((item, index) => {
            const productPayload = {
              qr_hotel: payload.qr_hotel,
              product_group: item.product_group,
              area: areaId || selectedRow?.id,
              name: item.name,
              name_translations: normalizeTranslations(item.name_translations, item.name),
              description: item.description || '',
              description_translations: normalizeTranslations(item.description_translations, item.description || ''),
              price: Number(item.price || 0),
              display_order: Number(item.display_order || index + 1),
              is_active: item.is_active !== false,
            };

            if (item.id) {
              return foodBeverageApi.updateProduct(item.id, productPayload);
            }
            return foodBeverageApi.createProduct(productPayload);
          })
        );
      }
      if (section === 'products') {
        const normalizedBody = syncPayloadTranslations(body, getEmptyPayload('products'));
        normalizedBody.price = Number(normalizedBody.price || 0);
        normalizedBody.name_translations = normalizeTranslations(
          normalizedBody.name_translations,
          normalizedBody.name || ''
        );
        normalizedBody.description_translations = normalizeTranslations(
          normalizedBody.description_translations,
          normalizedBody.description || ''
        );
        if (selectedRow) await foodBeverageApi.updateProduct(selectedRow.id, normalizedBody);
        else await foodBeverageApi.createProduct(normalizedBody);
      }
      await loadAll();
      setModalSuccessMessage('Kaydedildi.');
    } catch (err) {
      const errorText = err.message || 'Kaydetme işlemi başarısız.';
      setModalErrorMessage(errorText);
    } finally {
      setSaveLoading(false);
    }
  };

  const renderForm = () => {
    if (section === 'areas') {
      const availableGroups = payload.qr_hotel
        ? productGroups.filter((item) => String(item.qr_hotel) === String(payload.qr_hotel))
        : productGroups;

      const updateDraftProduct = (tempId, updates) => {
        setAreaDraftProducts((prev) =>
          prev.map((item) => (item.temp_id === tempId ? { ...item, ...updates } : item))
        );
      };

      const removeDraftProduct = (tempId) => {
        setAreaDraftProducts((prev) => prev.filter((item) => item.temp_id !== tempId));
        setAreaDraftSelectedTempIds((prev) => prev.filter((id) => id !== tempId));
      };

      const allDraftTempIds = areaDraftProducts.map((p) => p.temp_id);
      const allDraftsSelected =
        areaDraftProducts.length > 0 &&
        areaDraftSelectedTempIds.length === areaDraftProducts.length;

      const toggleSelectDraft = (tempId) => {
        setAreaDraftSelectedTempIds((prev) =>
          prev.includes(tempId) ? prev.filter((id) => id !== tempId) : [...prev, tempId]
        );
      };

      const toggleSelectAllDrafts = () => {
        if (allDraftsSelected) {
          setAreaDraftSelectedTempIds([]);
        } else {
          setAreaDraftSelectedTempIds([...allDraftTempIds]);
        }
      };

      const bulkRemoveSelectedDraftProducts = () => {
        if (areaDraftSelectedTempIds.length === 0) return;
        setAreaDraftProducts((prev) =>
          prev.filter((item) => !areaDraftSelectedTempIds.includes(item.temp_id))
        );
        setAreaDraftSelectedTempIds([]);
      };

      const addDraftProduct = () => {
        setAreaDraftProducts((prev) => [
          ...prev,
          {
            temp_id: `new-${Date.now()}-${prev.length}`,
            id: null,
            product_group: payload.product_group || '',
            name: '',
            name_translations: createEmptyTranslations(),
            description: '',
            description_translations: createEmptyTranslations(),
            price: 0,
            display_order: prev.length + 1,
            is_active: true,
          },
        ]);
      };

      return (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          <div className="lg:col-span-4 border border-gray-200 dark:border-gray-700 rounded-lg p-3 space-y-2">
            <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Alan Bilgisi</h4>

            <label className="text-xs text-gray-600 dark:text-gray-300">QR Otel</label>
            <select
              value={payload.qr_hotel || ''}
              onChange={(e) => setPayload((prev) => ({ ...prev, qr_hotel: e.target.value }))}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value="">QR otel seçiniz</option>
              {qrHotels.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>

            {renderTranslationFields({ title: 'Alan Adları', fieldName: 'name_translations' })}

            <label className="text-xs text-gray-600 dark:text-gray-300">Fotoğraf</label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files?.[0] || null;
                setPayload((prev) => ({ ...prev, image_file: file }));
              }}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 file:mr-3 file:px-2 file:py-1 file:rounded file:border-0 file:bg-slate-700 file:text-white"
            />
            {payload.image_file && (
              <p className="text-[11px] text-gray-500 dark:text-gray-400">{payload.image_file.name}</p>
            )}
            {imagePreviewUrl && (
              <img
                src={imagePreviewUrl}
                alt="Alan görseli"
                className="h-20 w-full object-cover rounded-md border border-gray-200 dark:border-gray-600"
              />
            )}

            <label className="text-xs text-gray-600 dark:text-gray-300">Varsayılan Ürün Grubu</label>
            <select
              value={payload.product_group || ''}
              onChange={(e) => setPayload((prev) => ({ ...prev, product_group: e.target.value }))}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value="">Grup seçiniz</option>
              {availableGroups.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>

            <label className="text-xs text-gray-600 dark:text-gray-300">Ürün Fotoğrafları</label>
            <button
              type="button"
              role="switch"
              aria-checked={payload.show_product_images !== false}
              onClick={() =>
                setPayload((prev) => ({
                  ...prev,
                  show_product_images: !(prev.show_product_images !== false),
                }))
              }
              className={`w-full flex items-center justify-between rounded-md border px-3 py-2 text-sm transition-colors ${
                payload.show_product_images !== false
                  ? 'border-blue-500 bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                  : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-700'
              }`}
            >
              <span>{payload.show_product_images !== false ? 'Açık' : 'Kapalı'}</span>
              <span
                className={`inline-flex h-5 w-10 rounded-full p-0.5 transition-colors ${
                  payload.show_product_images !== false ? 'bg-blue-600' : 'bg-gray-400 dark:bg-gray-500'
                }`}
              >
                <span
                  className={`h-4 w-4 rounded-full bg-white transition-transform ${
                    payload.show_product_images !== false ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </span>
            </button>

            <label className="text-xs text-gray-600 dark:text-gray-300">Ürün Fiyatları</label>
            <button
              type="button"
              role="switch"
              aria-checked={payload.show_product_prices !== false}
              onClick={() =>
                setPayload((prev) => ({
                  ...prev,
                  show_product_prices: !(prev.show_product_prices !== false),
                }))
              }
              className={`w-full flex items-center justify-between rounded-md border px-3 py-2 text-sm transition-colors ${
                payload.show_product_prices !== false
                  ? 'border-blue-500 bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                  : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-700'
              }`}
            >
              <span>{payload.show_product_prices !== false ? 'Açık' : 'Kapalı'}</span>
              <span
                className={`inline-flex h-5 w-10 rounded-full p-0.5 transition-colors ${
                  payload.show_product_prices !== false ? 'bg-blue-600' : 'bg-gray-400 dark:bg-gray-500'
                }`}
              >
                <span
                  className={`h-4 w-4 rounded-full bg-white transition-transform ${
                    payload.show_product_prices !== false ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </span>
            </button>

            <label className="text-xs text-gray-600 dark:text-gray-300">Sıra</label>
            <input
              type="number"
              value={payload.display_order ?? 1}
              onChange={(e) => setPayload((prev) => ({ ...prev, display_order: e.target.value }))}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
          </div>

          <div className="lg:col-span-8 border border-gray-200 dark:border-gray-700 rounded-lg p-3 space-y-3">
            <div className="max-h-[58vh] overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg mt-1">
              <div className="sticky top-0 z-40 px-3 py-2 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Alan Ürünleri</h4>
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={bulkRemoveSelectedDraftProducts}
                      disabled={areaDraftSelectedTempIds.length === 0}
                      className="px-2 py-1 rounded-md text-xs border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-40 disabled:pointer-events-none"
                    >
                      Seçilenleri sil ({areaDraftSelectedTempIds.length})
                    </button>
                    <button
                      type="button"
                      onClick={addDraftProduct}
                      className="px-2 py-1 rounded-md text-xs border border-blue-400 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                    >
                      + Ürün Ekle
                    </button>
                  </div>
                </div>
              </div>
              <table className="w-full text-xs">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-1 py-2 w-10 text-center text-gray-700 dark:text-white font-semibold">
                      <input
                        type="checkbox"
                        checked={allDraftsSelected}
                        onChange={toggleSelectAllDrafts}
                        disabled={areaDraftProducts.length === 0}
                        className="rounded border-gray-300 dark:border-gray-600"
                        title="Tümünü seç"
                        aria-label="Tümünü seç"
                      />
                    </th>
                    <th className="px-2 py-2 text-left text-gray-700 dark:text-white font-semibold">Ürün Grubu</th>
                    <th className="px-2 py-2 text-left text-gray-700 dark:text-white font-semibold">Ürün Adı</th>
                    <th className="px-2 py-2 text-left text-gray-700 dark:text-white font-semibold">Fiyat</th>
                    <th className="px-2 py-2 text-left text-gray-700 dark:text-white font-semibold">Sıra</th>
                    <th className="px-2 py-2 text-left text-gray-700 dark:text-white font-semibold">Aktif</th>
                    <th className="px-2 py-2 text-right text-gray-700 dark:text-white font-semibold">İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {areaDraftProducts.map((item) => (
                    <tr key={item.temp_id} className="border-t border-gray-200 dark:border-gray-700">
                      <td className="px-1 py-1 text-center">
                        <input
                          type="checkbox"
                          checked={areaDraftSelectedTempIds.includes(item.temp_id)}
                          onChange={() => toggleSelectDraft(item.temp_id)}
                          className="rounded border-gray-300 dark:border-gray-600"
                          aria-label="Satır seç"
                        />
                      </td>
                      <td className="px-2 py-1">
                        <select
                          value={item.product_group || ''}
                          onChange={(e) => updateDraftProduct(item.temp_id, { product_group: e.target.value })}
                          className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        >
                          <option value="">Grup</option>
                          {availableGroups.map((groupItem) => (
                            <option key={groupItem.id} value={groupItem.id}>
                              {groupItem.name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-2 py-1">
                        <input
                          value={item.name || ''}
                          onChange={(e) =>
                            updateDraftProduct(item.temp_id, {
                              name: e.target.value,
                              name_translations: normalizeTranslations(item.name_translations, e.target.value),
                            })
                          }
                          className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        />
                      </td>
                      <td className="px-2 py-1">
                        <input
                          type="number"
                          value={item.price ?? 0}
                          onChange={(e) => updateDraftProduct(item.temp_id, { price: e.target.value })}
                          className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        />
                      </td>
                      <td className="px-2 py-1">
                        <input
                          type="number"
                          value={item.display_order ?? 1}
                          onChange={(e) => updateDraftProduct(item.temp_id, { display_order: e.target.value })}
                          className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        />
                      </td>
                      <td className="px-2 py-1">
                        <select
                          value={item.is_active ? '1' : '0'}
                          onChange={(e) => updateDraftProduct(item.temp_id, { is_active: e.target.value === '1' })}
                          className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        >
                          <option value="1">Evet</option>
                          <option value="0">Hayır</option>
                        </select>
                      </td>
                      <td className="px-2 py-1 text-right">
                        <button
                          type="button"
                          onClick={() => removeDraftProduct(item.temp_id)}
                          className="px-2 py-1 rounded-md text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                        >
                          Sil
                        </button>
                      </td>
                    </tr>
                  ))}
                  {areaDraftProducts.length === 0 && (
                    <tr>
                      <td colSpan={7} className="px-3 py-3 text-center text-gray-500 dark:text-gray-400">
                        Henüz ürün eklenmedi.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      );
    }

    if (section === 'hotels') {
      return (
        <>
          <label className="text-xs text-gray-600 dark:text-gray-300">Sistem Oteli</label>
          <select
            value={payload.hotel_id || ''}
            onChange={(e) => setPayload((prev) => ({ ...prev, hotel_id: e.target.value }))}
            className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          >
            <option value="">Otel seçiniz</option>
            {systemHotels.map((item) => (
              <option key={item.id || item.hotel} value={item.id || item.hotel}>
                {item.hotel_name}
              </option>
            ))}
          </select>

          {renderTranslationFields({ title: 'QR Otel Adları', fieldName: 'name_translations' })}

          <label className="text-xs text-gray-600 dark:text-gray-300">QR Path</label>
          <input
            value={payload.qr_slug || ''}
            onChange={(e) => setPayload((prev) => ({ ...prev, qr_slug: e.target.value.toLowerCase() }))}
            placeholder="x, y, z gibi"
            className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500"
          />

          <label className="text-xs text-gray-600 dark:text-gray-300">Fotoğraf</label>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => {
              const file = e.target.files?.[0] || null;
              setPayload((prev) => ({ ...prev, image_file: file }));
            }}
            className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 file:mr-3 file:px-2 file:py-1 file:rounded file:border-0 file:bg-slate-700 file:text-white"
          />
          {payload.image_file && (
            <p className="text-[11px] text-gray-500 dark:text-gray-400">{payload.image_file.name}</p>
          )}
          {imagePreviewUrl && (
            <img
              src={imagePreviewUrl}
              alt="Otel görseli"
              className="h-20 w-full object-cover rounded-md border border-gray-200 dark:border-gray-600"
            />
          )}
        </>
      );
    }

    return (
      <>
        <label className="text-xs text-gray-600 dark:text-gray-300">QR Otel</label>
        <select
          value={payload.qr_hotel || ''}
          onChange={(e) => setPayload((prev) => ({ ...prev, qr_hotel: e.target.value }))}
          className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
        >
          <option value="">QR otel seçiniz</option>
          {qrHotels.map((item) => (
            <option key={item.id} value={item.id}>
              {item.name}
            </option>
          ))}
        </select>

        {renderTranslationFields({
          title: section === 'products' ? 'Ürün Adları' : section === 'product-groups' ? 'Grup Adları' : 'Adlar',
          fieldName: 'name_translations',
        })}

        {section === 'product-groups' && (
          <>
            <label className="text-xs text-gray-600 dark:text-gray-300">Fotoğraf</label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files?.[0] || null;
                setPayload((prev) => ({ ...prev, image_file: file }));
              }}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 file:mr-3 file:px-2 file:py-1 file:rounded file:border-0 file:bg-slate-700 file:text-white"
            />
            {payload.image_file && (
              <p className="text-[11px] text-gray-500 dark:text-gray-400">{payload.image_file.name}</p>
            )}
            {imagePreviewUrl && (
              <img
                src={imagePreviewUrl}
                alt="Ürün grubu görseli"
                className="h-20 w-full object-cover rounded-md border border-gray-200 dark:border-gray-600"
              />
            )}
          </>
        )}

        {section !== 'product-groups' && (
          <>
            <label className="text-xs text-gray-600 dark:text-gray-300">Ürün Grubu</label>
            <select
              value={payload.product_group || ''}
              onChange={(e) => {
                const selectedGroupId = e.target.value;
                const selectedGroup = productGroups.find(
                  (item) => String(item.id) === String(selectedGroupId)
                );

                setPayload((prev) => ({
                  ...prev,
                  product_group: selectedGroupId,
                  // QR otel seçilmediyse, seçilen grup üzerinden otomatik doldur.
                  qr_hotel: prev.qr_hotel || selectedGroup?.qr_hotel || '',
                }));
              }}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value="">Grup seçiniz</option>
              {productGroups
                .filter((item) =>
                  payload.qr_hotel
                    ? String(item.qr_hotel) === String(payload.qr_hotel)
                    : true
                )
                .map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
            </select>
          </>
        )}

        {section === 'products' && (
          <>
            <label className="text-xs text-gray-600 dark:text-gray-300">Alan</label>
            <select
              value={payload.area || ''}
              onChange={(e) => setPayload((prev) => ({ ...prev, area: e.target.value }))}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value="">Alan seçiniz</option>
              {areas
                .filter((item) => String(item.qr_hotel) === String(payload.qr_hotel))
                .map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
            </select>

            {renderTranslationFields({
              title: 'Ürün Açıklamaları',
              fieldName: 'description_translations',
              multiline: true,
            })}

            <label className="text-xs text-gray-600 dark:text-gray-300">Fiyat</label>
            <input
              type="number"
              value={payload.price ?? 0}
              onChange={(e) => setPayload((prev) => ({ ...prev, price: e.target.value }))}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />

            <label className="text-xs text-gray-600 dark:text-gray-300">Görsel URL</label>
            <input
              value={payload.image_url || ''}
              onChange={(e) => setPayload((prev) => ({ ...prev, image_url: e.target.value }))}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />

            <label className="text-xs text-gray-600 dark:text-gray-300">Aktif</label>
            <select
              value={payload.is_active ? '1' : '0'}
              onChange={(e) => setPayload((prev) => ({ ...prev, is_active: e.target.value === '1' }))}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value="1">Evet</option>
              <option value="0">Hayır</option>
            </select>
          </>
        )}

        <label className="text-xs text-gray-600 dark:text-gray-300">Sıra</label>
        <input
          type="number"
          value={payload.display_order ?? 1}
          onChange={(e) => setPayload((prev) => ({ ...prev, display_order: e.target.value }))}
          className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
        />
      </>
    );
  };

  const filterFields = useMemo(
    () => getFilterFieldsFromColumns(columns),
    [columns]
  );
  const filteredRows = useMemo(
    () => getFilteredRows(section, rows, filterFields),
    [section, rows, filterFields, filtersBySection]
  );

  const listTitle =
    section === 'hotels'
      ? 'QR Oteller'
      : section === 'product-groups'
        ? 'Ürün Grupları'
        : section === 'areas'
          ? 'Alanlar'
          : 'Ürünler';

  const translateSingleTitle =
    section === 'hotels'
      ? 'Seçili otel kaydını çevir (yalnızca otel adı)'
      : section === 'product-groups'
        ? 'Seçili ürün grubunu çevir'
        : section === 'areas'
          ? 'Seçili alanı çevir'
          : 'Seçili ürünü çevir';

  const toolbar = (
    <>
      <IconActionButton
        icon={LanguageIcon}
        onClick={handleTranslateCurrentRecord}
        disabled={!selectedRow || aiLoading}
        title={translateSingleTitle}
        className="text-slate-500 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400 disabled:text-gray-300"
      />
      {section === 'hotels' && (
        <IconActionButton
          icon={SparklesIcon}
          onClick={handleTranslateSelectedHotel}
          disabled={!selectedRow || aiLoading}
          title="Bu otelde tüm eksik çevirileri üret (gruplar, alanlar, ürünler)"
          className="text-slate-500 dark:text-slate-300 hover:text-amber-600 dark:hover:text-amber-400 disabled:text-gray-300"
        />
      )}
      {section === 'products' && (
        <>
          <IconActionButton
            icon={SparklesIcon}
            onClick={handleTranslateAllProductsMissing}
            disabled={aiLoading}
            title={
              selectedRow?.qr_hotel != null && selectedRow?.qr_hotel !== ''
                ? 'Seçili ürünün QR otelinde eksik ürün çevirilerini toplu üret (ad + açıklama)'
                : 'Tüm ürünlerde eksik çevirileri toplu üret (QR otel seçersen sadece o otel)'
            }
            className="text-slate-500 dark:text-slate-300 hover:text-amber-600 dark:hover:text-amber-400 disabled:text-gray-300"
          />
          <IconActionButton
            icon={SparklesIcon}
            onClick={handleTranslateAllProductDescriptionsMissing}
            disabled={aiLoading}
            title={
              selectedRow?.qr_hotel != null && selectedRow?.qr_hotel !== ''
                ? 'Seçili ürünün QR otelinde sadece eksik ürün açıklaması çevirilerini toplu üret'
                : 'Tüm ürünlerde sadece eksik açıklama çevirilerini toplu üret (QR otel seçersen sadece o otel)'
            }
            className="text-slate-500 dark:text-slate-300 hover:text-purple-600 dark:hover:text-purple-400 disabled:text-gray-300"
          />
        </>
      )}
      {section === 'areas' && (
        <>
          <IconActionButton
            icon={QrCodeIcon}
            onClick={handleGenerateQr}
            disabled={!selectedRow}
            title="QR Kod Oluştur"
            className="text-slate-500 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400 disabled:text-gray-300"
          />
          <IconActionButton
            icon={DocumentDuplicateIcon}
            onClick={openCopyProductsModal}
            disabled={!selectedRow}
            title="Alan ürünlerini kopyala"
            className="text-slate-500 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400 disabled:text-gray-300"
          />
        </>
      )}
      <IconActionButton
        icon={PlusIcon}
        onClick={openCreateModal}
        title="Yeni ekle"
      />
      <IconActionButton
        icon={TrashIcon}
        onClick={handleDelete}
        disabled={!selectedRow}
        title="Seçili kaydı sil"
        className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 disabled:text-gray-300"
      />
    </>
  );

  return (
    <div className="p-6 md:p-8 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Food & Beverage
        </h1>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          QR menü otelleri, ürün grupları, alanlar ve ürünleri yönetin.
        </p>
      </div>

      {error && (
        <div className="rounded-lg border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 px-4 py-2 text-sm">
          {error}
        </div>
      )}

      {successMessage && (
        <div className="rounded-lg border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200 px-4 py-2 text-sm flex items-start justify-between gap-2">
          <span>{successMessage}</span>
          <button
            type="button"
            onClick={() => setSuccessMessage('')}
            className="shrink-0 text-green-600 hover:text-green-800 dark:text-green-300"
            aria-label="Kapat"
          >
            ×
          </button>
        </div>
      )}

      <div className="space-y-6">
        <ActionToolbar>{toolbar}</ActionToolbar>
        {renderFilterPanel(section, filterFields, rows, null)}
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 md:p-6 shadow-sm">
          <div className="mb-3">
            <h2 className="font-semibold text-gray-900 dark:text-white">{listTitle}</h2>
          </div>
          <DataTable
            data={filteredRows}
            columns={columns}
            isLoading={isLoading}
            showSearch={false}
            compact
            enablePagination
            initialPageSize={25}
            onRowClick={setSelectedRow}
            onRowDoubleClick={openEditModal}
            selectedRowId={selectedRow?.id}
          />
        </div>
      </div>

      <ResponsiveModal
        isOpen={isModalOpen}
        onClose={closeMainModal}
        className={section === 'areas' ? 'max-w-6xl' : 'max-w-xl'}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {selectedRow ? 'Kayıt Düzenle' : 'Yeni Kayıt'}
          </h3>
          <button
            type="button"
            onClick={closeMainModal}
            className="p-1 rounded-md text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            Kapat
          </button>
        </div>
        <div className="p-6 space-y-4 max-h-[70vh] overflow-auto bg-white dark:bg-gray-800">
          {saveLoading && (
            <div className="rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 px-4 py-2 text-sm">
              Kaydediliyor...
            </div>
          )}
          {modalErrorMessage && (
            <div className="rounded-lg border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 px-4 py-2 text-sm flex items-start justify-between gap-2">
              <span>{modalErrorMessage}</span>
              <button
                type="button"
                onClick={() => setModalErrorMessage('')}
                className="shrink-0 text-red-600 hover:text-red-800 dark:text-red-300"
                aria-label="Kapat"
              >
                ×
              </button>
            </div>
          )}
          {modalSuccessMessage && (
            <div className="rounded-lg border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200 px-4 py-2 text-sm flex items-start justify-between gap-2">
              <span>{modalSuccessMessage}</span>
              <button
                type="button"
                onClick={() => setModalSuccessMessage('')}
                className="shrink-0 text-green-600 hover:text-green-800 dark:text-green-300"
                aria-label="Kapat"
              >
                ×
              </button>
            </div>
          )}
          {renderForm()}
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
          <button
            type="button"
            onClick={handleTranslateCurrentRecord}
            disabled={!selectedRow || aiLoading || saveLoading}
            className="px-4 py-2 rounded-md border border-blue-300 dark:border-blue-700 text-sm text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 disabled:opacity-50"
          >
            {aiLoading ? 'Çevriliyor...' : 'AI ile eksik çevirileri doldur'}
          </button>
          <button
            type="button"
            onClick={closeMainModal}
            className="px-4 py-2 rounded-md border border-gray-300 dark:border-gray-600 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            İptal
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saveLoading}
            className="px-4 py-2 rounded-md bg-blue-600 text-white text-sm hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {saveLoading ? 'Kaydediliyor...' : 'Kaydet'}
          </button>
        </div>
      </ResponsiveModal>

      <ResponsiveModal
        isOpen={copyModalOpen}
        onClose={() => {
          if (!copyLoading) setCopyModalOpen(false);
        }}
        className="max-w-md"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Alan ürünlerini kopyala
          </h3>
          <button
            type="button"
            onClick={() => !copyLoading && setCopyModalOpen(false)}
            className="p-1 rounded-md text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            Kapat
          </button>
        </div>
        <div className="p-6 space-y-4 bg-white dark:bg-gray-800">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Hedef alan:{' '}
            <span className="font-medium text-gray-900 dark:text-white">{selectedRow?.name || '—'}</span>
            {selectedRow?.hotel_name != null && selectedRow?.hotel_name !== '' && (
              <span className="text-gray-500 dark:text-gray-400"> ({selectedRow.hotel_name})</span>
            )}
            . Kaynak alandaki tüm ürünler bu alana eklenir; mevcut ürünler silinmez.
          </p>
          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Kaynak QR Otel</label>
            <select
              value={copySourceQrHotelId}
              onChange={(e) => {
                setCopySourceQrHotelId(e.target.value);
                setSourceAreaIdForCopy('');
              }}
              disabled={copyLoading}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
            >
              <option value="">Seçiniz</option>
              {qrHotels.map((h) => (
                <option key={h.id} value={h.id}>
                  {h.name}
                </option>
              ))}
            </select>
            {selectedRow &&
              copySourceQrHotelId &&
              String(copySourceQrHotelId) !== String(selectedRow.qr_hotel || '') && (
                <p className="text-[11px] text-amber-700 dark:text-amber-300 mt-1">
                  Kaynak ve hedef aynı QR otelde olmalıdır. Kopyalamak için kaynak oteli, hedef alanın oteli ile aynı seçin.
                </p>
              )}
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Kaynak alan</label>
            <select
              value={sourceAreaIdForCopy}
              onChange={(e) => setSourceAreaIdForCopy(e.target.value)}
              disabled={copyLoading || !copySourceQrHotelId}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
            >
              <option value="">{copySourceQrHotelId ? 'Alan seçiniz' : 'Önce QR otel seçiniz'}</option>
              {areas
                .filter(
                  (item) =>
                    selectedRow &&
                    String(item.qr_hotel) === String(copySourceQrHotelId) &&
                    String(item.id) !== String(selectedRow.id)
                )
                .map((item) => {
                  const sourceHotelName =
                    qrHotels.find((h) => String(h.id) === String(item.qr_hotel))?.name || item.hotel_name || '';
                  const label = sourceHotelName ? `${item.name} (${sourceHotelName})` : item.name;
                  return (
                    <option key={item.id} value={item.id}>
                      {label}
                    </option>
                  );
                })}
            </select>
          </div>
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
          <button
            type="button"
            onClick={() => !copyLoading && setCopyModalOpen(false)}
            className="px-4 py-2 rounded-md border border-gray-300 dark:border-gray-600 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            İptal
          </button>
          <button
            type="button"
            onClick={handleCopyProductsConfirm}
            disabled={
              copyLoading ||
              !sourceAreaIdForCopy ||
              !copySourceQrHotelId ||
              (selectedRow &&
                String(copySourceQrHotelId) !== String(selectedRow.qr_hotel || ''))
            }
            className="px-4 py-2 rounded-md bg-blue-600 text-white text-sm hover:bg-blue-700 disabled:opacity-50"
          >
            {copyLoading ? 'Kopyalanıyor...' : 'Kopyala'}
          </button>
        </div>
      </ResponsiveModal>

      <ResponsiveModal
        isOpen={qrModalOpen}
        onClose={() => {
          setQrModalOpen(false);
          setQrData(null);
          setQrLogoFile(null);
        }}
        className="max-w-md"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">QR Kod</h3>
          <button
            type="button"
            onClick={() => {
              setQrModalOpen(false);
              setQrData(null);
              setQrLogoFile(null);
            }}
            className="p-1 rounded-md text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            Kapat
          </button>
        </div>
        <div className="p-6 bg-white dark:bg-gray-800">
          {qrLoading ? (
            <div className="flex justify-center py-12 text-gray-500 dark:text-gray-400">Yükleniyor...</div>
          ) : qrData ? (
            <div className="space-y-4">
              <div className="flex justify-center">
                <img
                  src={qrData.image_base64}
                  alt={`QR - ${qrData.area_name}`}
                  className="w-48 h-48 object-contain border border-gray-200 dark:border-gray-600 rounded-lg"
                />
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                <span className="font-medium">{qrData.area_name}</span>
                {qrData.hotel_name && (
                  <span className="ml-1 text-gray-500 dark:text-gray-400">({qrData.hotel_name})</span>
                )}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 break-all">{qrData.url}</p>
              <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-600 dark:text-gray-300">
                <input
                  type="checkbox"
                  checked={qrWatermark}
                  onChange={handleQrWatermarkToggle}
                  disabled={qrLoading}
                  className="rounded border-gray-300 dark:border-gray-600"
                />
                <span>Kopyalamayı zorlaştırmak için üzerine silik logo ekle</span>
              </label>
              <input
                ref={qrLogoInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleQrLogoUpload}
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => qrLogoInputRef.current?.click()}
                  disabled={qrLoading}
                  className="flex-1 px-4 py-2 rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                >
                  {qrLoading ? 'Yükleniyor...' : 'Logo yükle'}
                </button>
                <button
                  type="button"
                  onClick={handleDownloadQr}
                  className="flex-1 px-4 py-2 rounded-md bg-slate-700 hover:bg-slate-600 text-white text-sm"
                >
                  QR Kodu İndir
                </button>
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400 py-4">
              Bir alan seçip QR Kod butonuna tıklayın.
            </p>
          )}
        </div>
      </ResponsiveModal>
    </div>
  );
}

export default FoodBeveragePage;
